import { useEffect, useMemo, useState } from "react";
import { api, type Paged, type Script, type ScriptRun, type ScriptTarget } from "../lib/api.js";
import { Drawer, Empty, Notice, Tabs, useAsync, Confirm } from "../components/ui.js";
import { dateTime, relativeTime, bytes } from "../lib/format.js";
import type { PageProps } from "./Shell.js";

/**
 * Scripts run on the machine through the same terminal channel a live session
 * uses, but driven by the server - so a run keeps going with no browser open,
 * and one script can be pushed to many machines at once.
 *
 * A machine is only a valid target when the access rules grant this user the
 * terminal capability on it *and* they have its password saved, since the
 * server authenticates as them.
 */
const INTERPRETERS = [
  { id: "powershell", label: "PowerShell", hint: "Windows" },
  { id: "cmd", label: "Command Prompt", hint: "Windows" },
  { id: "bash", label: "bash", hint: "Linux and macOS" },
  { id: "sh", label: "sh", hint: "Linux and macOS" },
];

const TERMINAL_STATES = ["ok", "failed", "refused", "timeout"];

function StatusTag({ status, exitCode }: { status: string; exitCode: number }) {
  const map: Record<string, { cls: string; label: string }> = {
    pending: { cls: "tag", label: "Queued" },
    running: { cls: "tag tag--warn", label: "Running" },
    ok: { cls: "tag tag--live", label: "Succeeded" },
    failed: { cls: "tag tag--deny", label: `Failed (${exitCode})` },
    refused: { cls: "tag tag--deny", label: "Refused" },
    timeout: { cls: "tag tag--deny", label: "Timed out" },
  };
  const m = map[status] ?? { cls: "tag", label: status };
  return <span className={m.cls}>{m.label}</span>;
}

export default function Scripts({ me, onToggleRail }: PageProps) {
  const [tab, setTab] = useState<"library" | "history">("library");
  const [batch, setBatch] = useState("");

  const canWrite = me.permissions.includes("*") || me.permissions.includes("script:write");
  const canRun = me.permissions.includes("*") || me.permissions.includes("script:run");

  return (
    <>
      <header className="topbar">
        <button className="btn btn--ghost btn--sm" onClick={onToggleRail} aria-label="Menu">&#9776;</button>
        <div className="topbar__title">
          <h1>Scripts</h1>
          <span className="topbar__hint">Run commands on machines you can reach</span>
        </div>
      </header>

      <div className="content">
        <div className="stack">
          <Tabs
            tabs={[{ id: "library" as const, label: "Library" }, { id: "history" as const, label: "History" }]}
            active={tab}
            onChange={(t) => { setTab(t); if (t === "library") setBatch(""); }}
          />
          {tab === "library" ? (
            <Library
              canWrite={canWrite}
              canRun={canRun}
              onRan={(b) => { setBatch(b); setTab("history"); }}
            />
          ) : (
            <History batch={batch} onClearBatch={() => setBatch("")} />
          )}
        </div>
      </div>
    </>
  );
}

// ------------------------------------------------------------- library ----

function Library({
  canWrite, canRun, onRan,
}: { canWrite: boolean; canRun: boolean; onRan: (batchId: string) => void }) {
  const [editing, setEditing] = useState<Script | "new" | null>(null);
  const [running, setRunning] = useState<Script | "adhoc" | null>(null);
  const [deleting, setDeleting] = useState<Script | null>(null);
  const [q, setQ] = useState("");
  const list = useAsync(
    () => api.get<Paged<Script>>(`/api/console/scripts?q=${encodeURIComponent(q)}`),
    [q],
  );

  return (
    <>
      <div className="row row--between">
        <input
          className="input"
          style={{ maxWidth: 280 }}
          placeholder="Search scripts"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <div className="row">
          {canRun ? (
            <button className="btn btn--sm" onClick={() => setRunning("adhoc")}>Run once</button>
          ) : null}
          {canWrite ? (
            <button className="btn btn--primary btn--sm" onClick={() => setEditing("new")}>New script</button>
          ) : null}
        </div>
      </div>

      {list.error ? <Notice>{list.error}</Notice> : null}

      <div className="notice">
        A script runs through the machine&rsquo;s terminal, as the account RustDesk runs under.
        The server signs in with the password you saved for that machine, so a machine with no
        saved password cannot be a target.
      </div>

      {!list.loading && !list.data?.data.length ? (
        <Empty
          title={q ? "No scripts match" : "No scripts yet"}
          hint="Add a script here once and run it on any machine you can reach."
          action={canWrite ? <button className="btn btn--primary" onClick={() => setEditing("new")}>Add a script</button> : null}
        />
      ) : (
        <div className="panel">
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Name</th><th>Runs with</th><th className="num">Size</th>
                  <th>Updated</th><th style={{ width: 170 }} />
                </tr>
              </thead>
              <tbody>
                {(list.data?.data ?? []).map((s) => (
                  <tr key={s.id}>
                    <td>
                      {s.name}
                      {s.note ? <div className="small faint">{s.note}</div> : null}
                    </td>
                    <td className="muted">
                      {INTERPRETERS.find((i) => i.id === s.interpreter)?.label ?? s.interpreter}
                    </td>
                    <td className="num muted">{bytes(s.size ?? s.content.length)}</td>
                    <td className="muted small">
                      {s.updated_at ? relativeTime(s.updated_at) : "-"}
                      {s.created_by_name ? <div className="faint">by {s.created_by_name}</div> : null}
                    </td>
                    <td className="actions">
                      {canRun ? (
                        <button className="btn btn--sm btn--primary" onClick={() => setRunning(s)}>Run</button>
                      ) : null}
                      <button className="btn btn--sm" onClick={() => setEditing(s)}>
                        {canWrite ? "Edit" : "View"}
                      </button>
                      {canWrite ? (
                        <button className="btn btn--sm btn--ghost" onClick={() => setDeleting(s)}>Delete</button>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {editing ? (
        <ScriptDrawer
          script={editing === "new" ? null : editing}
          readOnly={!canWrite}
          onClose={() => setEditing(null)}
          onSaved={() => { list.reload(); setEditing(null); }}
        />
      ) : null}

      {running ? (
        <RunDrawer
          script={running === "adhoc" ? null : running}
          onClose={() => setRunning(null)}
          onRan={(b) => { setRunning(null); onRan(b); }}
        />
      ) : null}

      {deleting ? (
        <Confirm
          title={`Delete "${deleting.name}"?`}
          body="Past runs and their output are kept."
          confirmLabel="Delete"
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            await api.del(`/api/console/scripts/${deleting.id}`);
            setDeleting(null);
            list.reload();
          }}
        />
      ) : null}
    </>
  );
}

function ScriptDrawer({
  script, readOnly, onClose, onSaved,
}: { script: Script | null; readOnly: boolean; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(script?.name ?? "");
  const [note, setNote] = useState(script?.note ?? "");
  const [interpreter, setInterpreter] = useState(script?.interpreter ?? "powershell");
  const [content, setContent] = useState(script?.content ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  /** Reads a dropped or picked file into the editor, so scripts can be uploaded. */
  const loadFile = async (file: File) => {
    if (file.size > 256 * 1024) {
      setError("That file is larger than the 256 KB limit");
      return;
    }
    setContent(await file.text());
    if (!name) setName(file.name);
    const ext = file.name.toLowerCase().split(".").pop();
    if (ext === "ps1") setInterpreter("powershell");
    else if (ext === "bat" || ext === "cmd") setInterpreter("cmd");
    else if (ext === "sh" || ext === "bash") setInterpreter("bash");
    setError("");
  };

  const save = async () => {
    setBusy(true);
    setError("");
    try {
      const body = { name, note, interpreter, content };
      if (script) await api.patch(`/api/console/scripts/${script.id}`, body);
      else await api.post("/api/console/scripts", body);
      onSaved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={script ? script.name : "New script"}
      onClose={onClose}
      footer={
        readOnly ? (
          <button className="btn" onClick={onClose}>Close</button>
        ) : (
          <>
            <button className="btn" onClick={onClose}>Cancel</button>
            <button
              className="btn btn--primary"
              disabled={busy || !name.trim() || !content.trim()}
              onClick={save}
            >
              {busy ? "Saving..." : "Save"}
            </button>
          </>
        )
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}

        <label className="field">
          <span>Name</span>
          <input
            className="input"
            value={name}
            disabled={readOnly}
            placeholder="restart-print-spooler.ps1"
            onChange={(e) => setName(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Note</span>
          <input
            className="input"
            value={note}
            disabled={readOnly}
            placeholder="What it does, and when to use it"
            onChange={(e) => setNote(e.target.value)}
          />
        </label>

        <label className="field">
          <span>Runs with</span>
          <select
            className="input"
            value={interpreter}
            disabled={readOnly}
            onChange={(e) => setInterpreter(e.target.value)}
          >
            {INTERPRETERS.map((i) => (
              <option key={i.id} value={i.id}>{i.label} &mdash; {i.hint}</option>
            ))}
          </select>
        </label>

        {!readOnly ? (
          <label className="field">
            <span>Upload a file</span>
            <input
              className="input"
              type="file"
              accept=".ps1,.bat,.cmd,.sh,.bash,.txt,text/plain"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void loadFile(f);
                e.target.value = "";
              }}
            />
          </label>
        ) : null}

        <label className="field">
          <span>Script</span>
          <textarea
            className="input mono"
            rows={18}
            spellCheck={false}
            value={content}
            disabled={readOnly}
            placeholder={"Write-Output \"hello\""}
            onChange={(e) => setContent(e.target.value)}
          />
        </label>
      </div>
    </Drawer>
  );
}

// ----------------------------------------------------------------- run ----

function RunDrawer({
  script, onClose, onRan,
}: { script: Script | null; onClose: () => void; onRan: (batchId: string) => void }) {
  const targets = useAsync(() => api.get<Paged<ScriptTarget>>("/api/console/scripts/targets"), []);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [q, setQ] = useState("");
  const [onlineOnly, setOnlineOnly] = useState(true);
  const [content, setContent] = useState(script?.content ?? "");
  const [interpreter, setInterpreter] = useState(script?.interpreter ?? "powershell");
  const [timeout, setTimeoutSeconds] = useState("300");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const rows = useMemo(() => {
    const all = targets.data?.data ?? [];
    const needle = q.trim().toLowerCase();
    return all.filter((t) => {
      if (onlineOnly && !t.online) return false;
      if (!needle) return true;
      return `${t.id} ${t.hostname} ${t.os}`.toLowerCase().includes(needle);
    });
  }, [targets.data, q, onlineOnly]);

  const eligible = rows.filter((t) => !t.blocked);
  const toggle = (id: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await api.post<{ batch_id: string }>("/api/console/scripts/run", {
        script_id: script?.id,
        name: script?.name,
        content: script ? undefined : content,
        interpreter: script ? undefined : interpreter,
        device_ids: [...picked],
        timeout_seconds: Number(timeout) || 300,
      });
      onRan(res.batch_id);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Drawer
      title={script ? `Run "${script.name}"` : "Run a command"}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button
            className="btn btn--primary"
            disabled={busy || !picked.size || (!script && !content.trim())}
            onClick={run}
          >
            {busy ? "Starting..." : `Run on ${picked.size} machine${picked.size === 1 ? "" : "s"}`}
          </button>
        </>
      }
    >
      <div className="stack">
        {error ? <Notice>{error}</Notice> : null}

        {!script ? (
          <>
            <label className="field">
              <span>Runs with</span>
              <select className="input" value={interpreter} onChange={(e) => setInterpreter(e.target.value)}>
                {INTERPRETERS.map((i) => (
                  <option key={i.id} value={i.id}>{i.label} &mdash; {i.hint}</option>
                ))}
              </select>
            </label>
            <label className="field">
              <span>Command</span>
              <textarea
                className="input mono"
                rows={8}
                spellCheck={false}
                value={content}
                onChange={(e) => setContent(e.target.value)}
              />
            </label>
          </>
        ) : null}

        <label className="field">
          <span>Give up after (seconds)</span>
          <input
            className="input"
            style={{ maxWidth: 140 }}
            value={timeout}
            inputMode="numeric"
            onChange={(e) => setTimeoutSeconds(e.target.value.replace(/[^0-9]/g, ""))}
          />
        </label>

        <div className="row row--between">
          <input
            className="input"
            style={{ maxWidth: 220 }}
            placeholder="Search machines"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <div className="row">
            <label className="row small" style={{ gap: 6 }}>
              <input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} />
              Online only
            </label>
            <button
              className="btn btn--sm"
              onClick={() => setPicked(new Set(eligible.map((t) => t.id)))}
            >
              Select all
            </button>
            <button className="btn btn--sm btn--ghost" onClick={() => setPicked(new Set())}>Clear</button>
          </div>
        </div>

        {targets.error ? <Notice>{targets.error}</Notice> : null}

        {targets.loading ? (
          <p className="faint small">Looking for machines you can reach&hellip;</p>
        ) : !rows.length ? (
          <Empty
            title="No machines available"
            hint="A machine can be a target once you have terminal access to it and have saved its password."
          />
        ) : (
          <div className="panel">
            <div className="tablewrap" style={{ maxHeight: 360, overflowY: "auto" }}>
              <table className="data">
                <tbody>
                  {rows.map((t) => (
                    <tr key={t.id}>
                      <td style={{ width: 34 }}>
                        <input
                          type="checkbox"
                          checked={picked.has(t.id)}
                          disabled={!!t.blocked}
                          onChange={() => toggle(t.id)}
                        />
                      </td>
                      <td>
                        {t.hostname || t.id}
                        <div className="small faint">{t.id}{t.os ? ` - ${t.os}` : ""}</div>
                      </td>
                      <td className="muted small">
                        {t.blocked
                          ? <span className="tag tag--deny">{t.blocked}</span>
                          : t.online ? <span className="tag tag--live">Online</span>
                            : <span className="tag">Offline</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div className="notice">
          Offline machines can be selected, but the run will fail: the script is delivered live,
          not queued for later.
        </div>
      </div>
    </Drawer>
  );
}

// ------------------------------------------------------------- history ----

function History({ batch, onClearBatch }: { batch: string; onClearBatch: () => void }) {
  const [open, setOpen] = useState<ScriptRun | null>(null);
  const runs = useAsync(
    () => api.get<Paged<ScriptRun>>(
      `/api/console/scripts/runs?pageSize=200${batch ? `&batch=${encodeURIComponent(batch)}` : ""}`,
    ),
    [batch],
  );

  // Poll only while something is still in flight, so an idle history page is
  // not hitting the server every few seconds.
  const pending = (runs.data?.data ?? []).some((r) => !TERMINAL_STATES.includes(r.status));
  useEffect(() => {
    if (!pending) return;
    const t = setInterval(() => runs.reload(), 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, batch]);

  // Keep the open run's output up to date while it is still running.
  useEffect(() => {
    if (!open || TERMINAL_STATES.includes(open.status)) return;
    const fresh = (runs.data?.data ?? []).find((r) => r.id === open.id);
    if (fresh && fresh !== open) setOpen(fresh);
  }, [runs.data, open]);

  return (
    <>
      {batch ? (
        <div className="row row--between">
          <span className="muted small">Showing one batch.</span>
          <button className="btn btn--sm" onClick={onClearBatch}>Show all runs</button>
        </div>
      ) : null}

      {runs.error ? <Notice>{runs.error}</Notice> : null}

      {!runs.loading && !runs.data?.data.length ? (
        <Empty title="Nothing has run yet" hint="Run a script from the library and it will appear here." />
      ) : (
        <div className="panel">
          <div className="tablewrap">
            <table className="data">
              <thead>
                <tr>
                  <th>Script</th><th>Machine</th><th>Result</th>
                  <th>Started</th><th className="num">Took</th><th style={{ width: 80 }} />
                </tr>
              </thead>
              <tbody>
                {(runs.data?.data ?? []).map((r) => (
                  <tr key={r.id}>
                    <td>
                      {r.script_name}
                      <div className="small faint">by {r.username}</div>
                    </td>
                    <td className="muted">{r.device_id}</td>
                    <td><StatusTag status={r.status} exitCode={r.exit_code} /></td>
                    <td className="muted small">{r.started_at ? dateTime(r.started_at) : "-"}</td>
                    <td className="num muted small">
                      {r.finished_at && r.started_at
                        ? `${((r.finished_at - r.started_at) / 1000).toFixed(1)}s`
                        : "-"}
                    </td>
                    <td className="actions">
                      <button className="btn btn--sm" onClick={() => setOpen(r)}>Output</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {open ? (
        <Drawer
          title={`${open.script_name} on ${open.device_id}`}
          onClose={() => setOpen(null)}
          footer={<button className="btn" onClick={() => setOpen(null)}>Close</button>}
        >
          <div className="stack">
            <div className="row">
              <StatusTag status={open.status} exitCode={open.exit_code} />
              <span className="muted small">
                {open.started_at ? dateTime(open.started_at) : "not started"}
              </span>
            </div>
            {open.error ? <Notice>{open.error}</Notice> : null}
            <pre className="codeblock" style={{ maxHeight: 460, overflow: "auto" }}>
              {open.output || (TERMINAL_STATES.includes(open.status) ? "No output." : "Waiting...")}
            </pre>
            {open.output ? (
              <button
                className="btn btn--sm"
                onClick={() => void navigator.clipboard?.writeText(open.output)}
              >
                Copy output
              </button>
            ) : null}
          </div>
        </Drawer>
      ) : null}
    </>
  );
}
